import React, { useState } from 'react';
import { useToast } from '../../../context/ToastContext';
import { toggleAssignmentTask } from '../../../services/assignmentService';
import { parseTasks, isTaskDone, countDone, isChecklist } from '../../../utils/assignmentTasks';
import styles from './AssignmentChecklist.module.css';

/**
 * An assignment's description as a checklist — one task per line.
 *
 * Used in three places with different rights, so `interactive` decides whether
 * the boxes do anything: the student owns the ticks, while a mentor or admin is
 * reading their progress. A read-only checklist still shows the boxes and the
 * count, because "2 of 5 done" is the thing a mentor opened the card for.
 *
 * A single-line description is not a list and renders as the plain paragraph it
 * always was — a lone checkbox next to one sentence makes an instruction look
 * like a task.
 */
const AssignmentChecklist = ({
  description,
  taskState,
  assignmentId,
  interactive = false,
  asMentor = false,
  collapsible = false,
  onChange,
}) => {
  const [open, setOpen] = useState(!collapsible);
  const toast = useToast();
  // Ticks are applied locally the moment they are clicked and rolled back if
  // the save fails. A checkbox that waits for a round trip before moving feels
  // broken, and this is the one control students will click repeatedly.
  const [localState, setLocalState] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const state = localState ?? taskState ?? {};
  const tasks = parseTasks(description);

  if (tasks.length === 0) return null;
  if (!isChecklist(description)) {
    return <p className={styles.plain}>{description}</p>;
  }

  const done = countDone(tasks, state);

  const toggle = async (task) => {
    if (!interactive || busyKey) return;
    const next = !isTaskDone(state, task.key);
    const optimistic = { ...state };
    if (next) optimistic[task.key] = new Date().toISOString();
    else delete optimistic[task.key];

    setLocalState(optimistic);
    setBusyKey(task.key);
    try {
      const updated = await toggleAssignmentTask(assignmentId, task.key, next, asMentor);
      setLocalState(updated.task_state || optimistic);
      if (onChange) onChange(updated);
    } catch (e) {
      setLocalState(state);   // put the box back where it was
      toast.error(e.response?.data?.detail || e.message || 'Could not save that tick.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className={styles.checklist}>
      <div
        className={collapsible ? styles.headToggle : styles.head}
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? open : undefined}
        onKeyDown={collapsible ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        } : undefined}
      >
        <span className={styles.count}>{done} of {tasks.length} done</span>
        <div className={styles.bar} aria-hidden="true">
          <div
            className={styles.barFill}
            style={{ width: `${Math.round((done / tasks.length) * 100)}%` }}
          />
        </div>
        {collapsible && <span className={styles.chevron} aria-hidden="true">{open ? '▲' : '▼'}</span>}
      </div>
      {open && <ul className={styles.list}>
        {tasks.map((task) => {
          const ticked = isTaskDone(state, task.key);
          return (
            <li key={task.key} className={ticked ? styles.itemDone : styles.item}>
              <label className={interactive ? styles.rowClickable : styles.row}>
                <input
                  type="checkbox"
                  className={styles.box}
                  checked={ticked}
                  disabled={!interactive || busyKey === task.key}
                  onChange={() => toggle(task)}
                />
                <span className={styles.text}>{task.text}</span>
              </label>
            </li>
          );
        })}
      </ul>}
    </div>
  );
};

export default AssignmentChecklist;
